import SelectList, { Item } from '@pelckmans/elementis/components/select-list';
import { SetStateAction, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CandidateGroup, fetchCandidateGroups } from '../../../../../api/groups';
import { SmartschoolSchool } from '../../../../../api/schools';
import AccountContext from '../../../../../contexts/AccountContext';
import { useRoutes } from '../../../../../hooks/useRoutes';
import useThrowAsyncError from '../../../../../hooks/useThrowAsyncError';
import { GroupsWithSchool } from '../../../context/MyGroupsContext';

type Props = {
  school: SmartschoolSchool | null;
  groupsWithSchool: GroupsWithSchool | null;
  errorText?: string;
  onChange: (selectedGroupIds: string[]) => void;
};

function GroupsSelectorSmartschoolSchool({ school, groupsWithSchool, errorText, onChange }: Props) {
  const { t } = useTranslation();
  const { getRoute } = useRoutes();
  const [candidateGroups, setCandidateGroups] = useState<CandidateGroup[]>([]);
  const [listOptions, setListOptions] = useState<Item[]>();
  const [isLoading, setIsLoading] = useState(true);
  const {
    account: { smartschoolSchools },
  } = useContext(AccountContext);
  const handleError = useThrowAsyncError();

  const handleSelectOption = (id: string, checked: boolean) => {
    const updatedOptions = listOptions!.map(option => (option.id === id ? { ...option, checked } : option));
    setListOptions(updatedOptions);
  };

  const handleSetItems = (items: SetStateAction<Item[]>) => {
    setListOptions(items as Item[]);
  };

  useEffect(() => {
    if (!listOptions) return;

    const selectedGroupIds = listOptions.filter(option => option.checked).map(option => option.id);

    onChange(selectedGroupIds);
  }, [listOptions, onChange]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const candidateGroups = await fetchCandidateGroups();

        setCandidateGroups(candidateGroups);
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [handleError]);

  useEffect(() => {
    if (isLoading) return;

    if (school) {
      const filteredCandidateGroups = candidateGroups.filter(group => group.smartschoolSchoolId === school.id);
      setListOptions(
        filteredCandidateGroups.map(group => ({
          id: group.smartschoolGroupId,
          value: group.name,
          checked: false,
          searchString: group.name,
        })),
      );
    } else {
      setListOptions([]);
    }
  }, [school, candidateGroups, isLoading]);

  let emptyMessage = undefined;

  const isStale = smartschoolSchools?.some(ss => ss.id === school?.id && ss.isSyncStale);
  const isSyncEnabled = smartschoolSchools?.some(ss => ss.id === school?.id && ss.oneRoster?.enabled);
  const areGroupsImportedForSchool = groupsWithSchool?.some(
    gws => gws.school.id === school?.id && gws.groups.length > 0,
  );

  if (!school) {
    emptyMessage = {
      text: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.PLACEHOLDER'),
    };
  } else if (isStale) {
    emptyMessage = {
      text: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ERROR.EMPTY'),
      alert: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ERROR.STALE'),
    };
  } else if (!isSyncEnabled) {
    emptyMessage = {
      text: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ERROR.EMPTY'),
      alert: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ERROR.SYNC', {
        domain: school.domain,
        helpdesk: getRoute('HELP'),
      }),
    };
    // There are no existing groups for this school and there are no candidate groups
  } else if (candidateGroups.length === 0 && !areGroupsImportedForSchool) {
    emptyMessage = {
      text: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ERROR.EMPTY'),
      alert: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ERROR.NO_GROUPS'),
    };
    // There are existing groups for this school and there are no more candidate groups for this school
  } else if (candidateGroups.length === 0 && areGroupsImportedForSchool) {
    emptyMessage = {
      text: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ERROR.EMPTY'),
      alert: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ERROR.ALL_GROUPS_ADDED'),
    };
  }

  return (
    <SelectList
      actions={{
        'aria-label': t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.ACTIONS.A11Y_LABEL'),
        include: true,
        translations: {
          checkAllText: t('FORM.SELECT_LIST.ACTIONS.CHECK_ALL'),
          unCheckAllText: t('FORM.SELECT_LIST.ACTIONS.UNCHECK_ALL'),
        },
      }}
      // https://pelckmans.atlassian.net/browse/PAKP-9897
      ariaLabels={{
        ariaLabelCheckList: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.A11Y_LABEL'),
        ariaLabelList: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.LIST.A11Y_LABEL'),
        ariaLabelLoading: t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.LOADING.A11Y_LABEL'),
        ariaLabelTextField: '',
      }}
      columns={2}
      labelText={t('PAGES.MY_GROUPS.MODALS.IMPORT_SMARTSCHOOL_GROUP.BODY.FORM.GROUP.LABEL')}
      items={listOptions ?? []}
      setItems={handleSetItems}
      empty={emptyMessage}
      isLoading={isLoading || !listOptions}
      error={!!errorText}
      errorText={errorText}
      loadingRows={2}
    >
      {(listOptions ?? []).map(item => (
        <SelectList.Item
          key={item.id}
          id={item.id}
          onChange={handleSelectOption}
          searchString={item.value}
          checked={item.checked}
        >
          {item.value}
        </SelectList.Item>
      ))}
    </SelectList>
  );
}

export default GroupsSelectorSmartschoolSchool;
